// Helpers that shape page content. The content itself comes from Supabase (see src/lib/api/collections.js).

// ---------- programs ----------
export const programPath = (p) => `/programs/${p.id}`;

const longLanguage = (short) =>
  ({ '3 languages': 'Sinhala, Tamil and English', 'Sin & Eng': 'Sinhala and English', Tamil: 'Tamil', English: 'English' }[short] || short);

const feeLevels = (s) => {
  const parts = String(s || '').replace(/\(one fee\)|⚠️/g, '').split('/').map((x) => x.trim()).filter(Boolean);
  return parts.map((x) => 'Rs. ' + x.replace(/[^\d,]/g, ''));
};
const groupSize = (p) => {
  const m = (p.forWhom || '').match(/Group size:?\s*(?:\*\*)?(\d+ to \d+)/);
  return m ? m[1] : null;
};

const CCCS_STANDARD =
  'Assistant Director (Coop), Center for Cooperative and Community Studies (CCCS), SANASA Campus, Paragammana, Hettimulla, Kegalle. Phone (+94) 71 206 32 05. Email sanasacampuscc@gmail.com.';
const CCCS_ENTREPRENEUR =
  'Assistant Director, Center for Cooperative and Community Studies (CCCS), SANASA Campus, Paragammana, Hettimulla, Kegalle. Phone 070 399 4599. Email assistdirect.scl.cccs@gmail.com.';

const DEGREE_APPLY =
  'Fill in the Application for the Degree Programme and number the three degrees 1, 2 and 3 in the order you prefer. Attach certified copies of your birth certificate, G.C.E. Advanced Level certificate, G.C.E. Ordinary Level certificate and school leaving certificate. Email it to asst.ar.ac@gmail.com, or post or hand it in to the Assistant Registrar (Academic), SANASA Campus, Paragammana, Hettimulla, Kegalle. For help, call 070 222 17 17.';
const DEGREE_PAY =
  'You can pay yourself (self-finance), find a sponsor, or use the government interest-free student loan. The loan notice on our site covered students who sat the G.C.E. A/L exam in 2019, 2020 or 2021. Call 070 222 17 17 or 070 399 45 99 to check if you qualify.';

const splitYears = (d) => d.subjects.map((s) => ({ h: s.year.replace(/^(\w+) year(, semester (\d))?$/i, (m, y, _s, n) => `${y} Year${n ? ', Semester ' + n : ''}`), paras: [s.list] }));

const degreeMeta = {
  'bsc-banking-finance': { modules: '40 modules + 1 research project', week: 'Mon-Thu classes, Fri-Sat work training', rows: ['TBC', '4 years', '40 + project', "Bachelor's", 'Mon-Thu', 'English'] },
  'bsc-insurance-risk': { modules: '30 modules + 1 research project', week: 'Mon-Wed work, Thu-Sat classes', rows: ['TBC', '4 years', '30 + project', "Bachelor's", 'Thu-Sat', 'English'] },
  'bsc-regional-science-planning': { modules: 'Geography, Economics, Planning, GIS', week: '4-year Honours degree', rows: ['TBC', '4 years', 'See Subjects', "Bachelor's", 'TBC', 'English'] },
};

// One shape for every program or degree page.
export const buildProgramPage = (p) => {
  if (!p) return null;
  const deg = p.sections;
  const apply = { to: `/apply?program=${p.id}`, label: 'Apply now' };
  if (p.kind === 'degree' && deg) {
    const dm = degreeMeta[p.id];
    const q = deg.quote.map((x) => x.replace(/\*\*/g, ''));
    return {
      title: p.title,
      tag: p.tag,
      meta: [dm.modules, dm.week, 'Taught in English'],
      tabs: [
        { label: 'Overview', blocks: [{ h: 'About This Degree', paras: q.slice(0, 2) }, ...(deg.week ? [{ h: 'How Your Week Works', paras: [deg.week + (deg.size ? ' ' + deg.size : '')] }] : []), { h: 'Why It Matters', paras: [q[2]] }] },
        { label: 'Subjects', blocks: splitYears(deg) },
        { label: 'Entry Rules', blocks: [{ h: 'Entry Requirements', paras: deg.entry }] },
        { label: 'How To Apply', blocks: [{ h: 'How To Apply', paras: [DEGREE_APPLY] }, { h: 'Paying For Your Studies', paras: [DEGREE_PAY] }] },
      ],
      sidebar: { label: 'Course fee', price: 'Ask us', note: 'Student loan available', button: apply, labels: ['Intake', 'Duration', 'Modules', 'Level', 'Classes', 'Language'], values: dm.rows },
    };
  }
  if (deg) {
    // Diploma and Higher Diploma in Insurance and Risk Management
    const hd = p.kind === 'higher-diploma';
    const entryFact = deg.facts.find((f) => /^Entry/.test(f));
    const detailFacts = deg.facts.filter((f) => f !== entryFact);
    return {
      title: p.title,
      tag: p.tag,
      meta: ['1 year, January and June intakes', 'Colombo, Galle, Kegalle, Kurunegala', 'Taught in English'],
      tabs: [
        { label: 'Overview', blocks: [{ h: 'About This Course', paras: deg.quote.map((x) => x.replace(/\*\*/g, '')) }] },
        { label: 'Details', blocks: [{ h: 'Length And Places', paras: detailFacts }] },
        { label: 'Entry Rules', blocks: [{ h: 'Entry Requirements', paras: [entryFact.replace(/^Entry \(exact\):\s*/, '')] }] },
        { label: 'How To Apply', blocks: [{ h: 'How To Apply', paras: ['Fill in the enrol form and send it to the campus. Call (+94) 35 493 51 26 or email asst.ar.ac@gmail.com for dates and fees.'] }] },
      ],
      sidebar: { label: 'Course fee', price: 'Ask us', note: 'Ask about payment options', button: { to: '/contact', label: 'Enroll now' }, labels: ['Intake', 'Duration', 'Places', 'Level', 'Classes', 'Language'], values: ['Jan & Jun', '1 year', '4 cities', hd ? 'Higher Dip.' : 'Diploma', 'Weekends', 'English'] },
    };
  }
  // CCCS training programs (1 to 46)
  const entre = p.n >= 36;
  const contactText = p.n === 4 ? 'Phone 071-5566525.' : entre ? CCCS_ENTREPRENEUR : CCCS_STANDARD;
  const levels = feeLevels(p.feeStructured);
  const levelsC = feeLevels(p.feeCustomized);
  const feeText = levels.length > 1
    ? `Structured program (on campus): Level 1 ${levels[0]}, Level 2 ${levels[1]}. Customized program (at your site): Level 1 ${levelsC[0]}, Level 2 ${levelsC[1]}.`
    : levelsC.length
      ? `Structured program (on campus): ${levels[0]}. Customized program (at your site): ${levelsC[0]}.`
      : `Fee: ${levels[0]}.`;
  const size = groupSize(p);
  const blocks = (arr) => arr.filter((b) => b.paras.every(Boolean));
  return {
    title: p.title,
    tag: p.tag,
    meta: [p.when.replace(/^Every /, 'Every ').replace(/ \(.*\)$/, ''), p.length, 'Taught in ' + longLanguage(p.language)],
    tabs: [
      { label: 'Overview', blocks: blocks([{ h: 'About This Program', paras: [p.summary] }, { h: 'You Will Be Able To', paras: [p.outcomes] }]) },
      { label: 'Topics', blocks: blocks([{ h: 'What You Study', paras: [p.topics] }, { h: 'How It Is Taught', paras: [p.how] }]).length ? blocks([{ h: 'What You Study', paras: [p.topics] }, { h: 'How It Is Taught', paras: [p.how] }]) : [{ h: 'Topics', paras: ['Ask the CCCS team for the full topic list.'] }] },
      { label: 'Who Can Join', blocks: blocks([{ h: 'Who It Is For', paras: [p.forWhom] }, { h: 'Aim', paras: [p.aim] }]).length ? blocks([{ h: 'Who It Is For', paras: [p.forWhom] }, { h: 'Aim', paras: [p.aim] }]) : [{ h: 'Who Can Join', paras: ['Ask the CCCS team who can join this program.'] }] },
      { label: 'Fees & Apply', blocks: [{ h: 'Fees', paras: [feeText] }, { h: 'How To Join', paras: ['Send the enrol form to CCCS. Structured programs run on the campus on fixed dates. Customized programs run at your own site on dates we agree with you.'] }, { h: 'Contact', paras: [contactText] }] },
    ],
    sidebar: { label: 'Course fee', price: p.price || 'Ask us', note: levelsC.length ? `At your site: ${levelsC[0]}${levelsC.length > 1 ? ' and up' : ''}` : 'One fee for everyone', button: { to: '/contact', label: 'Enroll now' }, labels: ['Schedule', 'Duration', 'Group', 'Language', 'Place', 'At your site'], values: [p.schedule, p.duration, size ? size.replace(' to ', '-') : 'TBC', p.language, 'Kegalle', levelsC[0] || '-'] },
  };
};

// ---------- people ----------
export const staffByDepartment = (staff) => {
  const groups = {};
  staff.forEach((s) => {
    (groups[s.department] = groups[s.department] || { dept: s.department, order: s.departmentOrder ?? 99, people: [] }).people.push(s);
  });
  return Object.values(groups).sort((a, b) => a.order - b.order);
};

// ---------- events, news, testimonials, faq ----------
export const MONTHS_SHORT = { January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr', May: 'May', June: 'Jun', July: 'Jul', August: 'Aug', September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec' };
